import React from 'react';
import { uniq } from 'lodash';

import ModalButton from './ModalButton';

const sections = [
  {
    key: 'common',
    label: '共通',
    items: [
      { title: '「税抜き」表示は一律10%で割り戻しています', body: '軽減税率（8%）の商品も10%で計算するため、モール側の税抜金額と少しずれます。' },
      { title: '広告費は広告レポートの金額（税抜）をもとにしています', body: '「税抜き」表示ではそのまま、税込み表示では10%を加算しています。' },
      { title: '経費・出店料・FBA保管料は月次PLにのみ計上されます', body: '日次PLの合計と月次PLの金額は一致しません。' },
      { title: '原価・配送料が未入力の商品は0円で計算されます', body: '商品分析の「原価未入力」で対象商品を確認できます。' },
      { title: '直近数日の数字は後から変わることがあります', body: 'キャンセルや取引の確定によって、確定済みの数字に置き換わります。' },
    ],
  },
  {
    key: 'amazon',
    label: 'Amazon',
    items: [
      { title: '注文日ではなく、決済が確定した日（出荷時点）で計上しています', body: 'セラーセントラルのビジネスレポートやダッシュボード（注文日基準・未出荷の注文を含む）とは日付も金額もずれます。ペイメントレポート（トランザクション）とは一致する考え方です。' },
      { title: '未出荷・保留中の注文は含みません', body: '決済が確定した取引のみを計上しています。' },
      { title: '返金は返金された日に計上されます', body: '元の注文日の売上は減りません。' },
      { title: '広告費はスポンサープロダクト広告のみです', body: 'スポンサーブランド・スポンサーディスプレイ広告は含みません。' },
      { title: '広告経由の売上は7日間のアトリビューションです', body: '広告コンソールで別の期間を選んでいる場合は一致しません。' },
      { title: 'FBA保管料は推定額です', body: '前月分が翌月中旬ごろに反映されます。長期保管料は含みません。' },
    ],
  },
  {
    key: 'rakuten',
    label: '楽天',
    items: [
      { title: '注文日ではなく、発送日で計上しています', body: 'RMSの売上データ（注文日基準）とは日付がずれます。' },
      { title: '売上は商品代金のみです', body: '送料・ラッピング料などは含みません。' },
      { title: 'システム利用料などの手数料は設定値からの推定です', body: '出店プラン・月商レンジ・平均バスケット単価の設定をもとに計算するため、実際の請求額とはずれます。' },
      { title: 'RPP広告経由の売上は720時間（30日）のアトリビューションです', body: 'Amazon広告（7日）とは基準が異なります。' },
    ],
  },
  {
    key: 'yahoo',
    label: 'Yahoo!ショッピング',
    items: [
      { title: '注文日で計上しています', body: '入金待ち・保留中の注文も含みます（キャンセルは除外）。' },
      { title: '売上は商品代金のみです', body: '送料・ギフト包装料・値引き・利用ポイントは含みません。' },
      { title: '手数料は料率からの推定です', body: 'ポイント原資・キャンペーン原資・アフィリエイト・決済手数料を料率で計算するため、実際の請求額とはずれます。' },
      { title: '広告費は含みません', body: 'ストアマッチなどの広告費は取り込んでいません。' },
    ],
  },
  {
    key: 'shopify',
    label: 'Shopify',
    items: [
      { title: '注文日で計上しています', body: '支払い済みの注文のみを含みます。' },
      { title: '返金は元の注文日に計上されます', body: '返金された日ではなく、注文日の売上から差し引きます。' },
      { title: '売上は商品代金のみです', body: '送料は含みません。' },
      { title: 'Shopify Payments以外の決済手数料は料率からの推定です', body: 'アプリの利用料は含みません。' },
    ],
  },
];

const productKpiSection = {
  key: 'productKpi',
  label: '損益（PL）との違い',
  items: [
    { title: '広告費は商品に紐付いた分のみです', body: '商品が特定できない広告費は損益にのみ計上されるため、商品ごとの合計と損益の広告費は一致しません。' },
    { title: '商品マスタに登録されていない商品は表示されません', body: '売上は損益に計上されます。「◯◯から商品を取得」で商品を取り込んでください。' },
    { title: 'Amazon Vine の出荷分は純売上から差し引いています', body: '損益の売上には含まれます。' },
  ],
};

export default function NumberDiscrepancyHelpButton({ sellers = [], shops = [], forProductKpi = false }) {
  const malls = uniq([...(sellers.length > 0 ? ['amazon'] : []), ...shops.map(_ => _.mall)]);
  const visibleSections = [
    ...(forProductKpi ? [productKpiSection] : []),
    ...sections.filter(_ => _.key === 'common' || malls.includes(_.key)),
  ];

  return (
    <ModalButton
      color="link"
      outline={false}
      size="sm"
      className="text-decoration-none text-nowrap"
      title="数字がずれていますか？"
      modalProps={{ size: 'lg', scrollable: true }}
      content={_ => (
        <div className="d-flex flex-column gap-4">
          <div className="small text-muted">
            モールの管理画面やレポートと数字が合わない場合、多くは集計の基準の違いによるものです。
          </div>
          {visibleSections.map(section => (
            <div key={section.key}>
              <div className="fw-bold border-bottom pb-1 mb-2">{section.label}</div>
              <ul className="mb-0 ps-3 d-flex flex-column gap-2">
                {section.items.map(item => (
                  <li key={item.title}>
                    <div>{item.title}</div>
                    <div className="small text-muted">{item.body}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    >
      <span className="far fa-question-circle me-1" />
      数字がずれていますか？
    </ModalButton>
  );
}
